import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Check, Trophy, AlertTriangle, Sparkles, Target, ChevronDown } from 'lucide-react';

/**
 * Enhanced statistics card component with animation and goal setting functionality
 */
const StatCard = ({ 
  title, 
  value, 
  icon, 
  color = '#4f46e5',
  bgColor, 
  isDarkMode,
  onClick,
  animationDelay = 0,
  allowGoalSetting = false,
  initialGoal = null,
  onGoalSet = () => {},
  deadline = null,
  unit = '',
  history = [],
  aiSuggestions = [],
  isLoading = false
}) => {
  const defaultBgLight = 'bg-white';
  const defaultBgDark = 'bg-gray-800';
  const [isSettingGoal, setIsSettingGoal] = useState(false);
  const [goal, setGoal] = useState(initialGoal);
  const [tempGoal, setTempGoal] = useState(initialGoal || '');
  const [tempDeadline, setTempDeadline] = useState(deadline || '');
  const [showGoalHistory, setShowGoalHistory] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showAiSuggestion, setShowAiSuggestion] = useState(false);
  const [suggestedGoal, setSuggestedGoal] = useState(null);
  const formRef = useRef(null);
  
  // Calculate progress if goal exists
  const progress = goal ? Math.min((parseInt(value) / parseInt(goal)) * 100, 100) : 0;
  
  // Days remaining until deadline
  const daysRemaining = deadline ? Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24)) : null;
  
  // Check if deadline is approaching (less than 3 days)
  const isDeadlineApproaching = daysRemaining !== null && daysRemaining > 0 && daysRemaining <= 3;
  
  // Check if deadline is passed
  const isDeadlinePassed = daysRemaining !== null && daysRemaining <= 0;

  // Get AI suggested goal based on past performance
  const getAiSuggestedGoal = () => {
    if (!aiSuggestions || aiSuggestions.length === 0) {
      // Default suggestion logic if no AI suggestions provided
      const currentValue = parseInt(value) || 0;
      // Suggest a goal that's about 10-20% higher than current value
      const incrementFactor = Math.random() * 0.1 + 0.1; // 10-20% increase
      const suggestedValue = Math.ceil(currentValue * (1 + incrementFactor));
      
      // Suggest a deadline 2-4 weeks from now
      const daysToAdd = Math.floor(Math.random() * 14) + 14; // 14-28 days
      const suggestedDeadline = new Date();
      suggestedDeadline.setDate(suggestedDeadline.getDate() + daysToAdd);
      
      return {
        value: suggestedValue,
        deadline: suggestedDeadline.toISOString().split('T')[0],
        reasoning: `Based on your current performance of ${currentValue} ${unit}, a ${Math.round(incrementFactor * 100)}% increase seems achievable in the next ${daysToAdd} days.`
      };
    }
    
    // Use provided AI suggestion (would ideally be from an AI model)
    return aiSuggestions[Math.floor(Math.random() * aiSuggestions.length)];
  };
  
  // Auto-save goal to localStorage
  useEffect(() => {
    if (goal) {
      localStorage.setItem(`stat_goal_${title}`, JSON.stringify({
        value: goal,
        deadline: tempDeadline
      }));
    }
  }, [goal, tempDeadline, title]);
  
  // Load goal from localStorage
  useEffect(() => {
    const savedGoal = localStorage.getItem(`stat_goal_${title}`);
    if (savedGoal && !initialGoal) {
      const parsed = JSON.parse(savedGoal);
      setGoal(parsed.value);
      setTempGoal(parsed.value);
      setTempDeadline(parsed.deadline || '');
    }
  }, [title, initialGoal]);
  
  // Show celebration effect when reaching 100% for the first time
  useEffect(() => {
    if (progress >= 100 && !showCelebration) {
      setShowCelebration(true);
      
      // Hide celebration after animation completes
      const timer = setTimeout(() => {
        setShowCelebration(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [progress, showCelebration]);
  
  // Close form when clicking outside
  useEffect(() => {
    if (!isSettingGoal) return;
    
    const handleClickOutside = (e) => {
      if (formRef.current && !formRef.current.contains(e.target)) {
        setIsSettingGoal(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSettingGoal]);

  const handleGoalSubmit = (e) => {
    e.preventDefault();
    if (tempGoal && !isNaN(tempGoal)) {
      setGoal(tempGoal);
      onGoalSet(tempGoal, tempDeadline);
      setIsSettingGoal(false);
      
      // Add to history if different from current goal
      if (tempGoal !== goal) {
        const newHistoryEntry = {
          date: new Date().toISOString(),
          value: tempGoal,
          deadline: tempDeadline
        };
        
        // Store updated history
        const currentHistory = JSON.parse(localStorage.getItem(`stat_goal_history_${title}`) || '[]');
        localStorage.setItem(`stat_goal_history_${title}`, JSON.stringify([...currentHistory, newHistoryEntry]));
      }
    }
  };
  
  const acceptSuggestedGoal = () => {
    setTempGoal(suggestedGoal.value.toString());
    setTempDeadline(suggestedGoal.deadline);
    setShowAiSuggestion(false);
    setIsSettingGoal(true);
  };

  const toggleGoalSetting = (e) => {
    e.stopPropagation();
    
    if (!isSettingGoal && !goal) {
      // When opening goal setting for a new goal, maybe show AI suggestion
      const shouldShowAiSuggestion = Math.random() > 0.3; // 70% chance
      if (shouldShowAiSuggestion) {
        setSuggestedGoal(getAiSuggestedGoal());
        setShowAiSuggestion(true);
        return;
      }
    }
    
    setIsSettingGoal(!isSettingGoal);
    setShowAiSuggestion(false);
  };
  
  const resetGoal = (e) => {
    e.stopPropagation();
    
    // Archive the goal completion in history
    if (goal) {
      const newHistoryEntry = {
        date: new Date().toISOString(),
        value: goal,
        completed: true,
        actualValue: value
      };
      
      // Store updated history
      const currentHistory = JSON.parse(localStorage.getItem(`stat_goal_history_${title}`) || '[]');
      localStorage.setItem(`stat_goal_history_${title}`, JSON.stringify([...currentHistory, newHistoryEntry]));
    }
    
    // Show AI suggestion for the next goal after completion
    setSuggestedGoal(getAiSuggestedGoal());
    setShowAiSuggestion(true);
    
    // Reset current goal
    setGoal(null);
    setTempGoal('');
    setTempDeadline('');
    localStorage.removeItem(`stat_goal_${title}`);
    onGoalSet(null);
  };
  
  const toggleHistoryView = (e) => {
    e.stopPropagation();
    setShowGoalHistory(!showGoalHistory);
  };
  
  // Get goal history from localStorage
  const goalHistory = JSON.parse(localStorage.getItem(`stat_goal_history_${title}`) || '[]');
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }).format(date);
  };
  
  // Motivational messages based on progress
  const getMotivationalMessage = () => {
    if (progress >= 100) return "Hedefine ulaştın! Harika iş!";
    if (progress >= 75) return "Son düzlüktesin!";
    if (progress >= 50) return "Yarı yoldan fazlasını tamamladın!";
    if (progress >= 25) return "İyi gidiyorsun, devam et!";
    return "Adım adım hedefe doğru!";
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.4, 
        delay: animationDelay,
        ease: "easeOut"
      }}
      whileHover={{ scale: 1.02 }}
      className={`p-4 rounded-xl shadow-sm glass-surface ${onClick ? 'cursor-pointer' : ''} relative overflow-hidden`}
      onClick={onClick}
    >
      {/* Loading skeleton or full content */}
      {isLoading ? (
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="h-8 w-8 rounded-full bg-gray-300/30 dark:bg-gray-600/30" />
              <div className="h-4 w-32 rounded bg-gray-300/30 dark:bg-gray-600/30" />
            </div>
            <div className="h-6 w-16 rounded bg-gray-300/30 dark:bg-gray-600/30" />
          </div>
          <div className="h-7 w-24 rounded bg-gray-300/30 dark:bg-gray-600/30 mb-3" />
          <div className="h-2 w-full rounded bg-gray-300/20 dark:bg-gray-600/20" />
        </div>
      ) : (
        <>
        {/* Celebration effect */}
        <AnimatePresence>
        {showCelebration && (
          <motion.div 
            className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{
                scale: [0, 1.2, 1],
                rotate: [0, 45, 0],
              }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="w-full h-full absolute"
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-full h-full relative">
                  {[...Array(20)].map((_, i) => {
                    const angle = (i / 20) * 360;
                    const delay = i * 0.05;
                    const length = Math.random() * 100 + 50;
                    const width = Math.random() * 6 + 2;
                    
                    return (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: 0, y: 0 }}
                        animate={{
                          opacity: [0, 1, 0],
                          x: [0, Math.cos(angle * Math.PI / 180) * length],
                          y: [0, Math.sin(angle * Math.PI / 180) * length]
                        }}
                        transition={{
                          duration: 1.5,
                          delay,
                          ease: "easeOut"
                        }}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: `${width}px`,
                          height: `${width}px`,
                          borderRadius: '50%',
                          backgroundColor: `hsl(${Math.random() * 360}, 80%, 60%)`,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </motion.div>
            <motion.div
              className="text-2xl font-bold text-emerald-500 bg-white dark:bg-gray-900 bg-opacity-80 dark:bg-opacity-80 px-4 py-2 rounded-lg z-10"
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Sparkles className="inline-block mr-2" />
              Hedef Başarıldı!
            </motion.div>
          </motion.div>
        )}
  </AnimatePresence>
      
  <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          {icon && (
            <div 
              className="p-2 rounded-full mr-2"
              style={{ backgroundColor: `${color}20` }} // 20% opacity version of color
            >
              {icon}
            </div>
          )}
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{title}</p>
        </div>
        
        {allowGoalSetting && (
          <div className="flex space-x-1">
            {goal && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleHistoryView}
                className="text-xs p-1 rounded-full"
                style={{ backgroundColor: `${color}20` }}
                title="Hedef geçmişini göster"
              >
                <Calendar size={14} color={color} />
              </motion.button>
            )}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleGoalSetting}
              className="text-xs px-2 py-1 rounded-full"
              style={{ backgroundColor: `${color}30` }}
            >
              {goal ? 'Değiştir' : 'Hedef Ekle'}
            </motion.button>
          </div>
        )}
      </div>
      
      <div className="flex items-baseline">
        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
        {unit && <span className={`ml-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{unit}</span>}
      </div>
      
      <AnimatePresence>
        {goal && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2"
          >
            <div className="flex justify-between items-center text-xs mb-1">
              <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                Hedef: {goal} {unit}
              </span>
              <div className="flex items-center">
                {isDeadlineApproaching && !isDeadlinePassed && (
                  <span className="text-amber-500 mr-2 flex items-center">
                    <AlertTriangle size={12} className="mr-1" />
                    {daysRemaining} gün
                  </span>
                )}
                {isDeadlinePassed && (
                  <span className="text-red-500 mr-2 flex items-center">
                    <AlertTriangle size={12} className="mr-1" />
                    Süresi doldu
                  </span>
                )}
                <span 
                  className={progress >= 100 ? 'text-green-500 font-bold' : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}
                >
                  %{Math.round(progress)}
                </span>
              </div>
            </div>
            
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="h-1.5 rounded-full" 
                style={{ backgroundColor: progress >= 100 ? '#10B981' : color }}
              />
            </div>
            
            {/* Motivational message */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className={`text-xs mt-1 font-medium ${
                progress >= 100 
                  ? 'text-green-500' 
                  : isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              {getMotivationalMessage()}
            </motion.div>
            
            {deadline && !isDeadlinePassed && (
              <div className="flex justify-end mt-1">
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {formatDate(deadline)}
                </span>
              </div>
            )}
            
            {progress >= 100 && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center justify-between mt-2"
              >
                <span className="text-green-500 text-xs font-medium flex items-center">
                  <Check size={14} className="mr-1" />
                  Hedefe ulaşıldı!
                </span>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={resetGoal}
                  className={`text-xs px-2 py-1 rounded-full ${
                    isDarkMode ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                  }`}
                >
                  <Trophy size={12} className="mr-1 inline-block" />
                  Yeni Hedef
                </motion.button>
              </motion.div>
            )}
          </motion.div>
        )}
        
        {/* AI Suggestion card */}
        {showAiSuggestion && suggestedGoal && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className={`mt-3 p-3 rounded-lg border ${
              isDarkMode 
                ? 'bg-gray-700/50 border-gray-600 text-gray-200' 
                : 'bg-blue-50 border-blue-200 text-gray-800'
            }`}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center">
                <div className={`p-1 rounded-full ${
                  isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'
                } mr-2`}>
                  <Target size={16} className={isDarkMode ? 'text-blue-300' : 'text-blue-600'} />
                </div>
                <p className="text-sm font-medium">AI Hedef Önerisi</p>
              </div>
            </div>
            
            <div className="mb-3">
              <div className="flex items-baseline mb-1">
                <span className="text-lg font-bold">{suggestedGoal.value}</span>
                <span className="text-xs ml-1">{unit}</span>
              </div>
              <p className="text-xs opacity-90">{suggestedGoal.reasoning}</p>
              <div className="text-xs mt-1 opacity-80">
                Önerilen bitiş tarihi: {formatDate(suggestedGoal.deadline)}
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button 
                onClick={() => setShowAiSuggestion(false)}
                className={`flex-1 py-1.5 text-xs rounded-md ${
                  isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                }`}
              >
                Reddet
              </button>
              <button 
                onClick={acceptSuggestedGoal}
                className="flex-1 py-1.5 text-xs bg-blue-600 text-white rounded-md"
              >
                Hedefi Kabul Et
              </button>
            </div>
          </motion.div>
        )}
        
        {/* Goal history panel */}
        {showGoalHistory && goalHistory.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`mt-3 p-2 rounded-md text-xs ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}
          >
            <h4 className="font-medium mb-1">Hedef Geçmişi</h4>
            <div className="max-h-32 overflow-y-auto">
              {goalHistory.slice().reverse().map((entry, idx) => (
                <div 
                  key={idx} 
                  className={`flex justify-between items-center py-1 ${
                    idx !== 0 ? 'border-t border-gray-200 dark:border-gray-600' : ''
                  }`}
                >
                  <div>
                    <span className="font-medium">{entry.value}</span>
                    {entry.completed && (
                      <span className="ml-1 text-green-500">✓</span>
                    )}
                  </div>
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                    {formatDate(entry.date)}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
        
        {/* Goal setting form */}
        {isSettingGoal && (
          <motion.form 
            ref={formRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            onSubmit={handleGoalSubmit}
            className="mt-3"
            onClick={e => e.stopPropagation()}
          >
            <div className="mb-2">
              <input 
                type="number" 
                value={tempGoal}
                onChange={(e) => setTempGoal(e.target.value)}
                className={`w-full p-2 text-sm rounded-md border mb-2 ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="Hedef değer"
                min="1"
                required
                autoFocus
              />
              
              <input 
                type="date" 
                value={tempDeadline}
                onChange={(e) => setTempDeadline(e.target.value)}
                className={`w-full p-2 text-sm rounded-md border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="Bitiş tarihi (opsiyonel)"
              />
            </div>
            
            <div className="flex space-x-2">
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIsSettingGoal(false);
                }}
                className={`flex-1 py-1.5 rounded-md text-sm ${
                  isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                }`}
              >
                İptal
              </button>
              <button 
                type="submit"
                className="flex-1 py-1.5 rounded-md text-white text-sm"
                style={{ backgroundColor: color }}
              >
                Kaydet
              </button>
            </div>
          </motion.form>
        )}
  </AnimatePresence>
  </>
  )}
    </motion.div>
  );
};

export default StatCard;